<template>
  <v-app class="try-model-app">
  <div class="try-model-page">
    <header class="try-header">
      <a
        class="try-brand"
        href="https://box21.ai"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="try-model-logo"
      >
        <img
          src="/api/static/box21_logo.svg"
          alt="Box21"
          class="try-brand-logo"
        />
      </a>
      <h1 class="try-title">{{ modelName || $t('live_demo.try_title') }}</h1>
      <p class="try-subtitle">{{ $t('live_demo.try_subtitle') }}</p>
    </header>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4" data-testid="try-model-error">
      {{ error }}
    </v-alert>
    <v-alert v-else-if="statusMessage" type="info" variant="tonal" class="mb-4" data-testid="try-model-status">
      {{ statusMessage }}
    </v-alert>

    <div class="try-toolbar" data-testid="try-model-toolbar">
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!cameraReady || streaming"
        data-testid="try-start-camera"
        @click="startCamera"
      >
        <v-icon start>mdi-video</v-icon>
        {{ $t('live_demo.start_camera') }}
      </v-btn>
      <v-btn
        variant="outlined"
        :disabled="!streaming"
        data-testid="try-stop-camera"
        @click="stopCamera"
      >
        <v-icon start>mdi-video-off</v-icon>
        {{ $t('live_demo.stop_camera') }}
      </v-btn>
      <v-btn
        variant="outlined"
        :disabled="!streaming || cameras.length < 2"
        data-testid="try-switch-camera"
        @click="switchCamera"
      >
        <v-icon start>mdi-camera-flip</v-icon>
        {{ $t('live_demo.switch_camera') }}
      </v-btn>
      <v-btn
        variant="outlined"
        :disabled="!streaming"
        data-testid="try-capture"
        @click="captureFrame"
      >
        <v-icon start>mdi-camera</v-icon>
        {{ $t('live_demo.take_photo') }}
      </v-btn>
      <v-btn
        variant="outlined"
        data-testid="try-pick-image"
        @click="$refs.fileInput.click()"
      >
        <v-icon start>mdi-image</v-icon>
        {{ $t('live_demo.pick_image') }}
      </v-btn>
      <span
        v-if="fpsDisplay"
        class="try-fps"
        data-testid="try-fps"
      >{{ fpsDisplay }}</span>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="d-none"
        data-testid="try-file-input"
        @change="onFilePicked"
      />
    </div>

    <div class="try-stage" data-testid="try-model-stage">
      <video
        ref="video"
        class="try-video"
        :class="{ 'd-none': !streaming || showStill }"
        playsinline
        muted
        autoplay
      />
      <canvas ref="overlay" class="try-canvas" data-testid="try-overlay" />
      <img
        v-if="showStill && stillUrl"
        ref="stillImg"
        :src="stillUrl"
        class="try-still"
        alt=""
        data-testid="try-still-image"
        @load="onStillLoaded"
      />
    </div>

    <div
      v-if="precisions.length"
      class="try-precision"
      data-testid="try-precision"
    >
      <span class="try-precision-label">{{ $t('live_demo.precision_label') }}</span>
      <div class="try-precision-btns" role="group" :aria-label="$t('live_demo.precision_label')">
        <button
          v-for="p in precisions"
          :key="p"
          type="button"
          class="try-precision-btn"
          :class="{ active: precision === p }"
          :data-testid="`try-precision-${p}`"
          :disabled="precisionLoading"
          @click="setPrecision(p)"
        >
          {{ precisionLabel(p) }}
        </button>
      </div>
      <p class="try-precision-hint">{{ $t('live_demo.precision_hint') }}</p>
    </div>

    <div
      v-if="isClassification && topPredictions.length"
      class="try-topk"
      data-testid="try-topk"
    >
      <h2 class="try-topk-title">{{ $t('live_demo.top_predictions') }}</h2>
      <ol class="try-topk-list">
        <li
          v-for="(pred, i) in topPredictions"
          :key="`${pred.classId}-${i}`"
          class="try-topk-item"
          :data-testid="`try-topk-item-${i}`"
        >
          <span class="try-topk-label">{{ pred.label }}</span>
          <span class="try-topk-score" data-testid="try-topk-score">
            {{ (pred.score * 100).toFixed(1) }}%
          </span>
          <div class="try-topk-bar-track">
            <div
              class="try-topk-bar"
              :style="{ width: `${Math.min(100, Math.max(0, pred.score * 100))}%` }"
            />
          </div>
        </li>
      </ol>
    </div>

    <div v-if="labels.length" class="try-labels" data-testid="try-labels">
      <span v-for="(label, i) in labels" :key="i" class="try-label-chip">{{ label }}</span>
    </div>
  </div>
  </v-app>
</template>

<script>
import { markRaw } from 'vue';
import {
  ensureLiteRt,
  loadDetectionModel,
  fetchTfliteBytes,
  runDetection,
  runClassification,
  letterboxDraw,
  classificationDraw,
  getModelInputSpec,
} from '@/utils/liveDemoInference';
import { resolveBrowserModelLabels } from '@/utils/modelLabels';

export default {
  name: 'TryModelPage',
  data() {
    return {
      token: '',
      modelName: '',
      labels: [],
      imageSize: [640, 640],
      tfliteUrl: '',
      error: '',
      statusMessage: '',
      // markRaw: Vue Proxy breaks LiteRT/Embind identity checks
      // ("Expected null or instance of TensorWrapper, got an instance of TensorWrapper").
      model: null,
      inputSpec: null,
      streaming: false,
      showStill: false,
      stillUrl: '',
      cameras: [],
      cameraIndex: 0,
      stream: null,
      cameraReady: typeof navigator !== 'undefined' && !!navigator.mediaDevices,
      rafId: null,
      runningInfer: false,
      confThreshold: 0.35,
      fps: 0,
      lastInferMs: 0,
      detectionCount: 0,
      modelType: '',
      task: 'detection',
      topPredictions: [],
      precisions: [],
      precision: 'float32',
      precisionLoading: false,
    };
  },
  computed: {
    isClassification() {
      return String(this.task || '').toLowerCase() === 'classification';
    },
    fpsDisplay() {
      if (!this.lastInferMs && !this.fps) {
        return '';
      }
      const fpsText = this.fps > 0 ? `${this.fps.toFixed(1)} FPS` : '';
      const msText = this.lastInferMs > 0 ? `${Math.round(this.lastInferMs)} ms` : '';
      const countText = this.isClassification
        ? `${this.topPredictions.length} top`
        : `${this.detectionCount} det`;
      return [fpsText, msText, countText].filter(Boolean).join(' · ');
    },
    tfliteUrlWithPrecision() {
      if (!this.tfliteUrl) {
        return '';
      }
      const sep = this.tfliteUrl.includes('?') ? '&' : '?';
      return `${this.tfliteUrl}${sep}precision=${encodeURIComponent(this.precision || 'float32')}`;
    },
  },
  async mounted() {
    this.token = this.$route.params.token;
    await this.bootstrap();
  },
  beforeUnmount() {
    this.stopLoop();
    this.stopCamera();
    if (this.stillUrl) {
      URL.revokeObjectURL(this.stillUrl);
    }
  },
  methods: {
    precisionLabel(p) {
      const key = `live_demo.precision_${p}`;
      const translated = this.$t(key);
      return translated === key ? p : translated;
    },
    async setPrecision(p) {
      if (!p || p === this.precision || this.precisionLoading) {
        return;
      }
      this.precision = p;
      await this.reloadModel();
      if (this.showStill && this.$refs.stillImg) {
        await this.onStillLoaded();
      } else if (this.streaming) {
        this.startLoop();
      }
    },
    async reloadModel() {
      this.precisionLoading = true;
      this.error = '';
      this.statusMessage = this.$t('live_demo.loading_model');
      try {
        const tfliteBytes = await fetchTfliteBytes(this.tfliteUrlWithPrecision);
        if (!this.labels.length) {
          this.labels = await resolveBrowserModelLabels({ tfliteBytes });
        }
        this.model = markRaw(await loadDetectionModel(tfliteBytes));
        const spec = markRaw(getModelInputSpec(this.model, this.imageSize));
        this.imageSize = [spec.height, spec.width];
        this.inputSpec = spec;
        this.statusMessage = this.$t('live_demo.ready');
      } catch (e) {
        const raw = e && e.message ? e.message : String(e);
        this.error = `${this.$t('live_demo.model_load_failed')}: ${raw}`;
        this.statusMessage = '';
      } finally {
        this.precisionLoading = false;
      }
    },
    async bootstrap() {
      this.statusMessage = this.$t('live_demo.loading');
      try {
        const metaRes = await fetch(`/api/public/live-demo/${encodeURIComponent(this.token)}`);
        if (!metaRes.ok) {
          throw new Error(this.$t('live_demo.not_found'));
        }
        const meta = await metaRes.json();
        this.modelName = meta.model_name || '';
        this.modelType = meta.model_type || '';
        this.task = meta.task
          || (/(fastai|convnext|mobilenet|vit_|resnet)/i.test(this.modelType)
            ? 'classification'
            : 'detection');
        this.labels = Array.isArray(meta.labels) ? meta.labels : [];
        if (Array.isArray(meta.image_size) && meta.image_size.length >= 2) {
          this.imageSize = [Number(meta.image_size[0]) || 640, Number(meta.image_size[1]) || 640];
        } else if (this.isClassification) {
          this.imageSize = [224, 224];
        }
        this.tfliteUrl = meta.tflite_url || `/api/public/live-demo/${this.token}/model.tflite`;
        this.precisions = Array.isArray(meta.precisions) ? meta.precisions.slice() : [];
        this.precision = meta.default_precision
          || (this.precisions[0] || 'float32');
        if (!meta.has_tflite) {
          this.error = this.$t('live_demo.tflite_missing');
          this.statusMessage = '';
          return;
        }
        this.statusMessage = this.$t('live_demo.loading_runtime');
        await ensureLiteRt();
        await this.reloadModel();
        await this.refreshCameras();
      } catch (e) {
        const raw = e && e.message ? e.message : String(e);
        if (/interpreter|tflite|compile|load/i.test(raw)) {
          this.error = `${this.$t('live_demo.model_load_failed')}: ${raw}`;
        } else {
          this.error = raw;
        }
        this.statusMessage = '';
      }
    },
    async refreshCameras() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.cameras = devices.filter((d) => d.kind === 'videoinput');
      } catch {
        this.cameras = [];
      }
    },
    async startCamera() {
      this.showStill = false;
      this.error = '';
      try {
        const constraints = {
          audio: false,
          video: this.cameras[this.cameraIndex]
            ? { deviceId: { exact: this.cameras[this.cameraIndex].deviceId } }
            : { facingMode: 'environment' },
        };
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        const video = this.$refs.video;
        video.srcObject = this.stream;
        await video.play();
        this.streaming = true;
        await this.refreshCameras();
        this.startLoop();
      } catch (e) {
        this.error = `${this.$t('live_demo.camera_failed')}: ${e.message || e}`;
      }
    },
    stopCamera() {
      this.stopLoop();
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
      const video = this.$refs.video;
      if (video) {
        video.srcObject = null;
      }
      this.streaming = false;
    },
    async switchCamera() {
      if (this.cameras.length < 2) {
        return;
      }
      this.cameraIndex = (this.cameraIndex + 1) % this.cameras.length;
      const wasStreaming = this.streaming;
      this.stopCamera();
      if (wasStreaming) {
        await this.startCamera();
      }
    },
    captureFrame() {
      const video = this.$refs.video;
      if (!video || !this.streaming) {
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) {
          return;
        }
        this.stopCamera();
        if (this.stillUrl) {
          URL.revokeObjectURL(this.stillUrl);
        }
        this.stillUrl = URL.createObjectURL(blob);
        this.showStill = true;
      }, 'image/jpeg', 0.92);
    },
    onFilePicked(ev) {
      const file = ev.target.files && ev.target.files[0];
      ev.target.value = '';
      if (!file) {
        return;
      }
      this.stopCamera();
      if (this.stillUrl) {
        URL.revokeObjectURL(this.stillUrl);
      }
      this.stillUrl = URL.createObjectURL(file);
      this.showStill = true;
    },
    async onStillLoaded() {
      const img = this.$refs.stillImg;
      if (!img || !this.model || this.runningInfer) {
        return;
      }
      this.runningInfer = true;
      try {
        // Yield so Vue can paint the still frame before LiteRT runs (keeps UI responsive).
        await new Promise((r) => requestAnimationFrame(() => r()));
        await this.inferSource(img, img.naturalWidth, img.naturalHeight);
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.runningInfer = false;
      }
    },
    startLoop() {
      this.stopLoop();
      const tick = async () => {
        if (!this.streaming || !this.model) {
          return;
        }
        const video = this.$refs.video;
        if (video && video.readyState >= 2 && !this.runningInfer) {
          this.runningInfer = true;
          try {
            await this.inferSource(video, video.videoWidth, video.videoHeight);
          } catch (e) {
            if (!this.error) {
              this.error = e.message || String(e);
            }
          } finally {
            this.runningInfer = false;
          }
        }
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    },
    stopLoop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },
    async inferSource(source, srcW, srcH) {
      if (!this.model || !srcW || !srcH) {
        return;
      }
      const t0 = (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
      const spec = this.inputSpec || getModelInputSpec(this.model, this.imageSize);
      const inH = spec.height;
      const inW = spec.width;

      if (this.isClassification) {
        // FastAI TFLite via onnx2tf: NHWC float with ImageNet normalize (model has no baked preprocess).
        const prep = classificationDraw(source, inW, inH, {
          layout: spec.layout,
          normalize: 'imagenet',
        });
        const preds = await runClassification(this.model, prep.tensor, {
          labels: this.labels,
          topK: 3,
          inputW: inW,
          inputH: inH,
          inputSpec: spec,
        });
        const t1 = (typeof performance !== 'undefined' && performance.now)
          ? performance.now()
          : Date.now();
        this.lastInferMs = t1 - t0;
        this.fps = this.lastInferMs > 0 ? 1000 / this.lastInferMs : 0;
        this.topPredictions = preds;
        this.detectionCount = 0;
        const overlay = this.$refs.overlay;
        const stage = overlay.parentElement;
        const displayW = stage.clientWidth;
        const scale = displayW / srcW;
        const displayH = Math.round(srcH * scale);
        overlay.width = displayW;
        overlay.height = displayH;
        overlay.style.width = `${displayW}px`;
        overlay.style.height = `${displayH}px`;
        const ctx = overlay.getContext('2d');
        ctx.clearRect(0, 0, displayW, displayH);
        if (!this.streaming) {
          ctx.drawImage(source, 0, 0, displayW, displayH);
        }
        if (preds[0]) {
          const label = `${preds[0].label} ${(preds[0].score * 100).toFixed(0)}%`;
          ctx.font = '16px sans-serif';
          const tw = ctx.measureText(label).width + 12;
          ctx.fillStyle = 'rgba(21, 101, 192, 0.9)';
          ctx.fillRect(8, 8, tw, 28);
          ctx.fillStyle = '#fff';
          ctx.fillText(label, 14, 28);
        }
        return;
      }

      // EdgeYOLO TFLite (onnx2tf) expects float RGB in 0–255, letterbox pad 114.
      const prep = letterboxDraw(source, inW, inH, {
        layout: spec.layout,
        normalize: 'uint8',
        padColor: 114,
      });
      const detections = await runDetection(this.model, prep.tensor, {
        labels: this.labels,
        confThreshold: this.confThreshold,
        inputW: inW,
        inputH: inH,
        inputSpec: spec,
        // INT8 TFLite often zeroes tiny box w/h — repair so LiteRT.js still shows boxes.
        repairCollapsed: this.precision === 'int8',
      });
      const t1 = (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
      this.lastInferMs = t1 - t0;
      this.fps = this.lastInferMs > 0 ? 1000 / this.lastInferMs : 0;
      this.detectionCount = detections.length;
      this.topPredictions = [];
      const overlay = this.$refs.overlay;
      const stage = overlay.parentElement;
      const displayW = stage.clientWidth;
      const scale = displayW / srcW;
      const displayH = Math.round(srcH * scale);
      overlay.width = displayW;
      overlay.height = displayH;
      overlay.style.width = `${displayW}px`;
      overlay.style.height = `${displayH}px`;
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, displayW, displayH);
      if (!this.streaming) {
        ctx.drawImage(source, 0, 0, displayW, displayH);
      }
      const invScale = 1 / prep.scale;
      const { padX, padY } = prep;
      ctx.lineWidth = 2;
      ctx.font = '14px sans-serif';
      for (const det of detections) {
        const x1 = (det.x1 - padX) * invScale * scale;
        const y1 = (det.y1 - padY) * invScale * scale;
        const x2 = (det.x2 - padX) * invScale * scale;
        const y2 = (det.y2 - padY) * invScale * scale;
        ctx.strokeStyle = '#00c853';
        ctx.fillStyle = 'rgba(0, 200, 83, 0.85)';
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        const label = `${det.label} ${(det.score * 100).toFixed(0)}%`;
        const tw = ctx.measureText(label).width + 8;
        ctx.fillRect(x1, Math.max(0, y1 - 18), tw, 18);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, x1 + 4, Math.max(12, y1 - 4));
      }
    },
  },
};
</script>

<style scoped>
.try-model-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}
.try-header {
  margin-bottom: 20px;
}
.try-brand {
  display: inline-block;
  line-height: 0;
}
.try-brand-logo {
  display: block;
  height: 40px;
  width: auto;
}
.try-title {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 8px 0 4px;
}
.try-subtitle {
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}
.try-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  align-items: center;
}
.try-fps {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  background: #e8f5e9;
  border-radius: 4px;
  padding: 4px 10px;
}
.try-stage {
  position: relative;
  width: 100%;
  background: #111;
  border-radius: 8px;
  overflow: hidden;
  min-height: 240px;
}
.try-precision {
  margin-top: 12px;
  padding: 10px 0 4px;
}
.try-precision-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 6px;
  color: rgba(0, 0, 0, 0.75);
}
.try-precision-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.try-precision-btn {
  border: 1px solid #b0bec5;
  background: #fff;
  color: #37474f;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.9rem;
  cursor: pointer;
}
.try-precision-btn.active {
  border-color: #1565c0;
  background: #e3f2fd;
  color: #0d47a1;
  font-weight: 600;
}
.try-precision-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.try-precision-hint {
  margin: 6px 0 0;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.55);
}
.try-video,
.try-still,
.try-canvas {
  display: block;
  width: 100%;
  height: auto;
}
.try-canvas {
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
}
.try-still {
  position: relative;
  z-index: 0;
}
.try-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
.try-label-chip {
  background: #e8eaf6;
  color: #1a237e;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.85rem;
}
.try-topk {
  margin-top: 16px;
  padding: 12px 14px;
  background: #f5f7fa;
  border-radius: 8px;
}
.try-topk-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 10px;
}
.try-topk-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.try-topk-item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 2px 12px;
  margin-bottom: 10px;
}
.try-topk-item:last-child {
  margin-bottom: 0;
}
.try-topk-label {
  font-weight: 500;
}
.try-topk-score {
  font-variant-numeric: tabular-nums;
  color: #1565c0;
  font-weight: 600;
}
.try-topk-bar-track {
  grid-column: 1 / -1;
  height: 6px;
  background: #cfd8dc;
  border-radius: 3px;
  overflow: hidden;
}
.try-topk-bar {
  height: 100%;
  background: #1565c0;
  border-radius: 3px;
}
</style>
